import { useMutation } from '@apollo/client';
import { useRef } from 'react';

import {
  NetworkContactRequestSendDocument,
  NetworkDeclineRecommendationDocument,
} from '@xing-com/crate-network-profile-card';

import { useSearchAndShuffleContext } from '../../context';
import { useActionsHelper } from '../../use-actions-helper';

import { useVomp } from './use-vomp';

type UseMemberCardActionsReturn = {
  memberCardSwiping: (
    deckIndex: number,
    actionType: 'left' | 'right' | 'super'
  ) => void;
  memberCardDecline: (deckIndex: number) => void;
  memberCardSendRequest: (deckIndex: number) => void;
};

export const useMemberCardActions = (): UseMemberCardActionsReturn => {
  const { setSwipeAction, removeCardFromDeck } = useActionsHelper();
  const { callVomp } = useVomp();
  const { decks, setExecutingAction, setStatusBanner } =
    useSearchAndShuffleContext();

  const memberCardDecline = (deckIndex: number): void => {
    setSwipeAction(deckIndex, 'left');
  };

  const memberCardSendRequest = (deckIndex: number): void => {
    setSwipeAction(deckIndex, 'right');
  };

  const lastRequestRef = useRef<{ displayName: string } | null>(null);

  const [sendContactRequest] = useMutation(NetworkContactRequestSendDocument, {
    errorPolicy: 'all',
    onCompleted: (data) => {
      if (!data.networkContactRequestsSend?.error?.message) {
        setStatusBanner({
          show: true,
          key: 'NETWORK_SEARCH_AND_SWIPE_PRIVACY_CONTACT_REQUEST_SUCESS_MESSAGE',
          type: 'success',
        });
      } else if (
        data.networkContactRequestsSend?.error?.message ===
        'CONTACT_LIMIT_REACHED'
      ) {
        setStatusBanner({
          show: true,
          key: 'NETWORK_SEARCH_AND_SWIPE_PRIVACY_CONTACT_REQUEST_FAILURE_MESSAGE',
          type: 'error',
        });
      } else {
        setStatusBanner({
          show: true,
          key: 'NETWORK_SEARCH_AND_SWIPE_PRIVACY_CONTACT_REQUEST_FAILURE_MESSAGE',
          type: 'error',
        });
      }
    },
    onError: () => {
      setStatusBanner({
        show: true,
        key: 'NETWORK_SEARCH_AND_SWIPE_PRIVACY_CONTACT_REQUEST_FAILURE_MESSAGE',
        type: 'error',
      });
    },
  });

  const [declineRecommendation] = useMutation(
    NetworkDeclineRecommendationDocument,
    {
      errorPolicy: 'all',
    }
  );

  const memberCardSwiping = (
    deckIndex: number,
    actionType: 'left' | 'right' | 'super'
  ): void => {
    setExecutingAction(true);
    const card = decks[deckIndex].cards[0];
    if (card.type !== 'Member') {
      setExecutingAction(false);
      return;
    }

    callVomp(card.data.profileId);
    if (actionType === 'right' || actionType === 'super') {
      if (card.type === 'Member') {
        lastRequestRef.current = {
          displayName: card.data.displayName,
        };
        sendContactRequest({
          variables: {
            input: {
              recipientId: card.data.profileId || '',
            },
          },
        });
      }
    } else if (actionType === 'left') {
      if (card.type === 'Member') {
        declineRecommendation({
          variables: {
            recommendationId: card.data.profileId || '',
          },
        });
      }
    }

    setTimeout(() => {
      removeCardFromDeck(deckIndex, 0);
      setExecutingAction(false);
    }, 500);
  };

  return {
    memberCardSwiping,
    memberCardDecline,
    memberCardSendRequest,
  };
};
